import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import OnboardingPage from './pages/OnboardingPage';
import SourcesListPage from './pages/SourcesListPage';
import SourcesArchivePage from './pages/SourcesArchivePage';
import SourceDetailRouter from './pages/SourceDetailRouter';
import PartnerPortalPage from './pages/PartnerPortalPage';
import WorkspacePage from './pages/WorkspacePage';
import WorkspacesPage from './pages/WorkspacesPage';
import AuditPage from './pages/AuditPage';
import LandingPage from './pages/LandingPage';
import DatasetBuilderPage from './pages/DatasetBuilderPage';
import { ToastProvider } from './components/Toast';
import CommandPalette from './components/CommandPalette';
import RateLimitBanner from './components/RateLimitBanner';
import { getMe } from './api';
import type { UserPublic } from '@datahub/shared';
import './styles/tokens.css';

// AuthGate — fetches /api/auth/me on mount. While loading shows nothing
// (avoids a flash of LandingPage for already-signed-in users).  Once
// resolved, picks LandingPage when null and the requested page when
// authenticated. /portal/:token + / (LandingPage when signed-out) stay
// public; everything else is auth-gated.
function AuthGate(): React.ReactElement {
  const [user, setUser] = useState<UserPublic | null | undefined>(undefined);
  useEffect(() => {
    getMe().then(setUser).catch(() => setUser(null));
  }, []);
  if (user === undefined) {
    return <div style={{ minHeight: '100vh' }} />;
  }
  return (
    <Routes>
      <Route path="/portal/:token" element={<PartnerPortalPage />} />
      {user
        ? <>
            <Route path="/" element={<OnboardingPage />} />
            <Route path="/sources" element={<SourcesListPage />} />
            <Route path="/sources/archive" element={<SourcesArchivePage />} />
            <Route path="/sources/:id" element={<SourceDetailRouter />} />
            <Route path="/workspace" element={<WorkspacePage />} />
            <Route path="/workspaces" element={<WorkspacesPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/datasets/new" element={<DatasetBuilderPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        : <>
            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
      }
    </Routes>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <ToastProvider>
        <RateLimitBanner />
        <CommandPalette />
        <AuthGate />
      </ToastProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
